import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import { SplashScreen, SplashScreenResponse, SplashScreensResponse } from '@/src/types/apps/splashScreens'

export function extractSplashFromApiResponse(payload: unknown): SplashScreen | null {
  if (!payload || typeof payload !== 'object') {
    return null
  }

  const layer1 = (payload as { data?: SplashScreen }).data ?? (payload as SplashScreen)
  const layer2 =
    layer1 && typeof layer1 === 'object' && 'data' in layer1
      ? ((layer1 as { data?: SplashScreen }).data ?? (layer1 as SplashScreen))
      : (layer1 as SplashScreen)

  return layer2?.id != null ? layer2 : null
}

interface SplashScreensState {
  splashScreens: SplashScreen[]
  splashScreen: SplashScreen | null
  loading: boolean
  meta: SplashScreensResponse['meta']
  error: unknown
  success: string | null
}

const initialState: SplashScreensState = {
  splashScreens: [],
  splashScreen: null,
  loading: false,
  meta: {
    total: 0,
    pages: 0
  },
  error: null,
  success: null
}

type QueryOptions = {
  take?: number
  skip?: number
  sortBy?: string
  filters?: Record<string, string>
}

export const getSplashScreens = createAsyncThunk<
  SplashScreensResponse,
  QueryOptions,
  { rejectValue: unknown; extra: { httpClient: unknown } }
>('gygSplashScreens/getSplashScreens', async (params, { rejectWithValue, extra }) => {
  try {
    const response = await (extra.httpClient as { get: Function }).get('splash-screens', { params })

    return response.data
  } catch (error: unknown) {
    return rejectWithValue((error as { response?: { data?: unknown } })?.response?.data ?? error)
  }
})

export const getSplashScreen = createAsyncThunk<
  SplashScreenResponse,
  number,
  { rejectValue: unknown; extra: { httpClient: unknown } }
>('gygSplashScreens/getSplashScreen', async (id, { rejectWithValue, extra }) => {
  try {
    const response = await (extra.httpClient as { get: Function }).get(`splash-screens/${id}`)

    return response.data
  } catch (error: unknown) {
    return rejectWithValue((error as { response?: { data?: unknown } })?.response?.data ?? error)
  }
})

export const createSplashScreen = createAsyncThunk<
  SplashScreenResponse,
  Partial<SplashScreen>,
  { rejectValue: unknown; extra: { httpClient: unknown } }
>('gygSplashScreens/createSplashScreen', async (data, { rejectWithValue, extra }) => {
  try {
    const response = await (extra.httpClient as { post: Function }).post('splash-screens', data)

    return response.data
  } catch (error: unknown) {
    return rejectWithValue((error as { response?: { data?: unknown } })?.response?.data ?? error)
  }
})

export const updateSplashScreen = createAsyncThunk<
  SplashScreenResponse,
  { id: number; data: Partial<SplashScreen> },
  { rejectValue: unknown; extra: { httpClient: unknown } }
>('gygSplashScreens/updateSplashScreen', async ({ id, data }, { rejectWithValue, extra }) => {
  try {
    const response = await (extra.httpClient as { put: Function }).put(`splash-screens/${id}`, data)

    return response.data
  } catch (error: unknown) {
    return rejectWithValue((error as { response?: { data?: unknown } })?.response?.data ?? error)
  }
})

export const deleteSplashScreen = createAsyncThunk<
  { id: number },
  number,
  { rejectValue: unknown; extra: { httpClient: unknown } }
>('gygSplashScreens/deleteSplashScreen', async (id, { rejectWithValue, extra }) => {
  try {
    await (extra.httpClient as { delete: Function }).delete(`splash-screens/${id}`)

    return { id }
  } catch (error: unknown) {
    return rejectWithValue((error as { response?: { data?: unknown } })?.response?.data ?? error)
  }
})

export const uploadSplashScreenImage = createAsyncThunk<
  SplashScreenResponse,
  { id: number; file: File },
  { rejectValue: unknown; extra: { httpClient: unknown } }
>('gygSplashScreens/uploadSplashScreenImage', async ({ id, file }, { rejectWithValue, extra }) => {
  try {
    const formData = new FormData()
    formData.append('file', file)

    const response = await (extra.httpClient as { post: Function }).post(`splash-screens/${id}/media`, formData, {
      timeout: 120_000
    })

    return response.data
  } catch (error: unknown) {
    return rejectWithValue((error as { response?: { data?: unknown } })?.response?.data ?? error)
  }
})

const gygSplashScreensSlice = createSlice({
  name: 'gygSplashScreens',
  initialState,
  reducers: {
    clearSplashScreenSuccess(state) {
      state.success = null
    },
    clearSplashScreenError(state) {
      state.error = null
    }
  },
  extraReducers: builder => {
    builder.addCase(getSplashScreens.pending, state => {
      state.loading = true
      state.error = null
    })
    builder.addCase(getSplashScreens.fulfilled, (state, action) => {
      state.loading = false
      state.splashScreens = action.payload.data
      state.meta = action.payload.meta
    })
    builder.addCase(getSplashScreens.rejected, (state, action) => {
      state.loading = false
      state.error = action.payload
    })

    builder.addCase(getSplashScreen.pending, state => {
      state.loading = true
      state.error = null
      state.splashScreen = null
    })
    builder.addCase(getSplashScreen.fulfilled, (state, action) => {
      state.loading = false
      state.splashScreen = extractSplashFromApiResponse(action.payload)
    })
    builder.addCase(getSplashScreen.rejected, (state, action) => {
      state.loading = false
      state.error = action.payload
    })

    builder.addCase(createSplashScreen.pending, state => {
      state.loading = true
      state.error = null
    })
    builder.addCase(createSplashScreen.fulfilled, state => {
      state.loading = false
      state.success = 'Splash screen created successfully'
    })
    builder.addCase(createSplashScreen.rejected, (state, action) => {
      state.loading = false
      state.error = action.payload
    })

    builder.addCase(updateSplashScreen.pending, state => {
      state.loading = true
      state.error = null
    })
    builder.addCase(updateSplashScreen.fulfilled, (state, action) => {
      state.loading = false
      state.splashScreen = extractSplashFromApiResponse(action.payload) ?? state.splashScreen
      state.success = 'Splash screen updated successfully'
    })
    builder.addCase(updateSplashScreen.rejected, (state, action) => {
      state.loading = false
      state.error = action.payload
    })

    builder.addCase(deleteSplashScreen.pending, state => {
      state.loading = true
      state.error = null
    })
    builder.addCase(deleteSplashScreen.fulfilled, (state, action) => {
      state.loading = false
      state.splashScreens = state.splashScreens.filter(item => item.id !== action.payload.id)
      state.meta.total = Math.max(0, state.meta.total - 1)
      state.success = 'Splash screen deleted successfully'
    })
    builder.addCase(deleteSplashScreen.rejected, (state, action) => {
      state.loading = false
      state.error = action.payload
    })

    builder.addCase(uploadSplashScreenImage.fulfilled, (state, action) => {
      state.splashScreen = extractSplashFromApiResponse(action.payload) ?? state.splashScreen
    })
  }
})

export const { clearSplashScreenSuccess, clearSplashScreenError } = gygSplashScreensSlice.actions
export default gygSplashScreensSlice.reducer
